import { useState } from 'react';
import { Shield, Award, TrendingUp, Eye, BarChart3 } from 'lucide-react';
import { 
  SAFEScoreGauge, 
  StakeholderBenefits, 
  SAFEScoreExplainer,
  LiveRankingsPreview 
} from '../components/visuals/SAFEScoreComponents';
import { SAFEScoreDashboard } from '../components/visuals/SAFEScoreDashboard';
import { CertificationInterestForm } from '../components/forms/CertificationInterestForm';
import { SafeLabsCTA } from '../components/SafeLabsCTA';

const SAFEScorePage = () => {
  const [showCertForm, setShowCertForm] = useState(false);

  return (
    <div data-testid="safescore-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption text-amber-400 mb-4">Trust Infrastructure</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
                SAFEScore
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8">
                Like Trustpilot for e-commerce or BBB ratings — an independent, real-time measure of how effectively platforms protect children online.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                  <span className="text-sm">Real-time scoring</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                  <Eye className="w-5 h-5 text-amber-400" />
                  <span className="text-sm">Public rankings</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  <span className="text-sm">Independent verification</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-white/5 border border-white/20 p-12 text-center">
                <SAFEScoreGauge score={85} label="Example SAFEScore" />
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-6">
                  Independently Verified
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section-padding bg-cyan-700 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xs uppercase tracking-widest text-cyan-200 mb-4">The Vision</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6 max-w-3xl mx-auto">
            Every platform serving children will have a public SAFEScore — updated in real-time, verified independently.
          </h2>
          <p className="text-lg text-cyan-100 max-w-2xl mx-auto">
            Parents check Trustpilot before buying. Schools check ratings before procuring. Why shouldn't we have the same transparency for child safety?
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="caption text-cyan-700 mb-4">How It Works</p>
              <h2 className="heading-2 mb-6">Transparent, Independent Scoring</h2>
              <p className="body-large mb-8">
                The SAFEScore is calculated from multiple factors — all independently verified by SAFE Labs. No platform can self-certify or manipulate their score.
              </p>
              <div className="space-y-4">
                {[
                  'Continuous monitoring, not annual snapshots',
                  'Adversarial testing against real threat patterns',
                  'Public methodology — fully transparent',
                  'Historical trend visibility'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white border border-slate-200 p-4">
                    <BarChart3 className="w-5 h-5 text-cyan-700 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <SAFEScoreExplainer />
          </div>
        </div>
      </section>

      {/* Live Rankings Preview */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <LiveRankingsPreview />
        </div>
      </section>

      {/* Interactive Dashboard */}
      <section className="section-padding bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-12">
            <p className="caption text-cyan-700 mb-4">Interactive Dashboard</p>
            <h2 className="heading-2 mb-4">Explore Platform Performance</h2>
            <p className="body-large max-w-2xl mx-auto">
              Filter by platform type, location, and age group to see real-time safeguarding metrics. Click any platform for detailed threat data.
            </p>
          </div>
          <SAFEScoreDashboard />
        </div>
      </section>

      {/* Benefits for All */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-12">
            <p className="caption text-cyan-400 mb-4">Universal Value</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              Benefits for Everyone
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              The SAFEScore creates value across the entire ecosystem — from parents making daily decisions to regulators shaping policy.
            </p>
          </div>
        </div>
      </section>

      {/* Stakeholder Benefits Grid */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto container-padding">
          <StakeholderBenefits />
        </div>
      </section>

      {/* For Platforms */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption text-amber-600 mb-4">For Platforms</p>
              <h2 className="heading-2 mb-6">Your Competitive Advantage</h2>
              <p className="body-large mb-8">
                A high SAFEScore isn't just compliance — it's a competitive advantage. Just like a 5-star Trustpilot rating drives business, a top-tier SAFEScore signals to users, investors, and regulators that you take safety seriously.
              </p>
              <div className="bg-slate-900 text-white p-6">
                <p className="text-amber-400 font-semibold mb-2">Display Your Score</p>
                <p className="text-slate-300 text-sm">
                  Certified platforms receive an embeddable trust badge — displaying their real-time SAFEScore on their own site, in app stores, and marketing materials.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-8">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-6">Trust Badge Example</p>
              <div className="bg-white border-2 border-slate-200 p-6 flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-12 h-12 text-cyan-700" />
                  <div>
                    <p className="font-bold text-slate-900">SAFE Verified</p>
                    <p className="text-sm text-slate-500">SAFEScore: 87</p>
                  </div>
                </div>
                <div className="ml-auto">
                  <div className="w-16 h-16 rounded-full border-4 border-emerald-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-emerald-600">87</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center">
                Embeddable badge updates automatically with your current score
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-amber-600 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            Get Your Platform Scored
          </h2>
          <p className="text-lg text-amber-100 max-w-2xl mx-auto mb-10">
            Join the certification process and become one of the first platforms to display a verified SAFEScore.
          </p>
          <button 
            onClick={() => setShowCertForm(true)}
            className="bg-white text-amber-700 px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-amber-50 transition-colors"
            data-testid="btn-safescore-certification"
          >
            Register for Certification
          </button>
        </div>
      </section>

      <CertificationInterestForm 
        isOpen={showCertForm} 
        onClose={() => setShowCertForm(false)} 
      />
      <SafeLabsCTA />
    </div>
  );
};

export default SAFEScorePage;
